import type { CreateScanResponse, ScanLogDto, UnlockableField } from "@mvk/shared";
import type { ScanLog, SavedContact, ScanTargetType } from "../types";
import { isApiEnabled } from "../api/config";
import { visitorAuthStorage } from "../api/authStorage";
import { scanApi, companyDetailToUiCompany } from "../api/scan";
import {
  mapProductSummaryToProduct,
  mapRepresentativeSummaryToRepresentative,
} from "./catalogService";
import { localClientState } from "../state/localClientState";
import type { ScanResult } from "./codeRecognitionService";
import { eventPackService } from "./eventPackService";
import { outboxService } from "./outboxService";
import { appConfig } from "../config";
import { engagementService } from "./engagementService";

export interface UniversalScanResult {
  success: boolean;
  scanResult: ScanResult & {
    company?: ReturnType<typeof companyDetailToUiCompany>;
    companies?: ReturnType<typeof companyDetailToUiCompany>[];
    booth?: string;
    representative?: ReturnType<typeof mapRepresentativeSummaryToRepresentative>;
    product?: ReturnType<typeof mapProductSummaryToProduct>;
    visitor?: {
      id: string;
      fullName: string;
      phone?: string;
      email?: string;
      companyName?: string;
      position?: string;
      city?: string;
      activityType?: string;
      interestedProducts?: string;
    };
  };
  scanLog: ScanLog;
  savedContact?: SavedContact;
  alreadyScanned: boolean;
  offlineQueued?: boolean;
  leadId?: string;
}

function mapDtoToScanLog(
  dto: ScanLogDto & {
    company?: {
      id: string;
      name: string;
      category?: string;
      booth?: string;
      hall?: string;
      phone?: string;
      email?: string;
      website?: string;
    };
    visitor?: {
      id: string;
      fullName: string;
      phone?: string;
      email?: string;
      companyName?: string;
      position?: string;
      city?: string;
      activityType?: string;
      interestedProducts?: string;
    };
    linkedRepresentative?: {
      id: string;
      fullName: string;
      position?: string;
      companyId?: string;
    };
  },
): ScanLog {
  return {
    id: dto.id,
    userId: dto.userId,
    eventId: dto.eventId,
    scanTargetType: dto.scanTargetType,
    scanTargetId: dto.scanTargetId,
    codeType: dto.codeType,
    codeValue: dto.codeValue,
    scannedAt: dto.scannedAt,
    companyId: dto.companyId,
    representativeId: dto.representativeId,
    productId: dto.productId,
    unlockedFields: dto.unlockedFields,
    notes: dto.notes,
    tags: dto.tags,
    company: dto.company
      ? {
          id: dto.company.id,
          name: dto.company.name,
          category: dto.company.category,
          booth: dto.company.booth,
          hall: dto.company.hall,
          phone: dto.company.phone,
          email: dto.company.email,
          website: dto.company.website,
        }
      : undefined,
    visitor: dto.visitor,
    linkedRepresentative: dto.linkedRepresentative
      ? {
          id: dto.linkedRepresentative.id,
          fullName: dto.linkedRepresentative.fullName,
          position: dto.linkedRepresentative.position,
          companyId: dto.linkedRepresentative.companyId,
        }
      : undefined,
  };
}

function mapApiResponse(
  response: CreateScanResponse,
  userId: string,
): UniversalScanResult {
  const company = response.target.company
    ? companyDetailToUiCompany(response.target.company)
    : undefined;

  const companies = (response.target.companies ?? []).map(companyDetailToUiCompany);
  const sharedBooth = companies.length > 1;

  const representative = response.target.representative
    ? mapRepresentativeSummaryToRepresentative(response.target.representative)
    : undefined;

  const product = response.target.product
    ? mapProductSummaryToProduct(response.target.product)
    : undefined;

  const visitor = response.target.visitor
    ? {
        id: response.target.visitor.id,
        fullName: response.target.visitor.fullName,
        phone: response.target.visitor.phone,
        email: response.target.visitor.email,
        companyName: response.target.visitor.companyName,
        position: response.target.visitor.position,
        city: response.target.visitor.city,
        activityType: response.target.visitor.activityType,
        interestedProducts: response.target.visitor.interestedProducts,
      }
    : undefined;

  const scanResult = {
    targetType: response.target.targetType,
    targetId: response.target.targetId,
    codeType: response.scan.codeType,
    codeValue: response.scan.codeValue,
    company: sharedBooth ? undefined : company,
    companies: sharedBooth ? companies : undefined,
    booth: response.target.booth ?? company?.booth,
    representative,
    product,
    visitor,
  };

  const scanLog = mapDtoToScanLog(response.scan);
  scanLog.userId = userId;

  let savedContact: SavedContact | undefined;
  if (company && !sharedBooth) {
    savedContact = {
      id: `sc-${response.scan.id}`,
      userId,
      eventId: response.scan.eventId,
      companyId: company.id,
      savedAt: response.scan.scannedAt,
    };
  }

  return {
    success: true,
    scanResult,
    scanLog,
    savedContact,
    alreadyScanned: response.alreadyScanned,
    leadId: response.leadId,
  };
}

async function resolveOffline(
  eventId: string,
  userId: string,
  code: string,
  codeType: "qr" | "barcode",
): Promise<UniversalScanResult | null> {
  if (!appConfig.featureFlags.enableOfflineMode) return null;

  const entry = await eventPackService.resolveCode(eventId, code);
  if (!entry) return null;

  const pack = await eventPackService.getCached(eventId);
  if (!pack) return null;

  const companyDetail = entry.companyId
    ? pack.companies.find((c) => c.id === entry.companyId)
    : undefined;
  const productSummary = entry.productId
    ? pack.products.find((p) => p.id === entry.productId)
    : undefined;
  const repSummary = entry.representativeId
    ? pack.representatives.find((r) => r.id === entry.representativeId)
    : undefined;

  const company = companyDetail ? companyDetailToUiCompany(companyDetail) : undefined;
  const product = productSummary ? mapProductSummaryToProduct(productSummary) : undefined;
  const representative = repSummary
    ? mapRepresentativeSummaryToRepresentative(repSummary)
    : undefined;

  const unlockedFields: UnlockableField[] = [];
  if (company?.contact.phone) unlockedFields.push("phone");
  if (company?.contact.email) unlockedFields.push("email");
  if (company?.website) unlockedFields.push("website");
  if (representative?.publicPhone || representative?.phone) unlockedFields.push("phone");
  if (representative?.email) unlockedFields.push("email");
  if (representative?.telegram) unlockedFields.push("telegram");

  const scannedAt = new Date().toISOString();
  const idempotencyKey =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `scan-off-${Date.now()}`;

  await outboxService.enqueueScan({
    eventId,
    codeValue: code.trim(),
    codeType,
    scannedAt,
    idempotencyKey,
  });

  if (company) {
    void engagementService.track({
      eventId,
      kind: "scan_contact",
      targetType: entry.targetType,
      targetId: entry.targetId,
      companyId: company.id,
    });
  }

  const scanLog: ScanLog = {
    id: `local-${idempotencyKey}`,
    userId,
    eventId,
    scanTargetType: entry.targetType,
    scanTargetId: entry.targetId,
    codeType,
    codeValue: code.trim(),
    scannedAt,
    companyId: entry.companyId,
    representativeId: entry.representativeId,
    productId: entry.productId,
    unlockedFields: [...new Set(unlockedFields)],
  };

  const savedContact: SavedContact | undefined = company
    ? {
        id: `sc-${scanLog.id}`,
        userId,
        eventId,
        companyId: company.id,
        savedAt: scannedAt,
      }
    : undefined;

  return {
    success: true,
    scanResult: {
      targetType: entry.targetType,
      targetId: entry.targetId,
      codeType,
      codeValue: code.trim(),
      company,
      representative,
      product,
    },
    scanLog,
    savedContact,
    alreadyScanned: false,
    offlineQueued: true,
  };
}

export const scanService = {
  async scanCode(
    code: string,
    options: { eventId?: string; userId?: string; codeType?: "qr" | "barcode" } = {},
  ): Promise<UniversalScanResult | null> {
    const eventId = options.eventId ?? localClientState.getSelectedEventId() ?? "";
    const userId = options.userId ?? localClientState.getUserProfile().id;
    const codeType = options.codeType ?? "qr";

    if (!eventId) {
      throw new Error("[scanService] eventId required");
    }

    const online =
      isApiEnabled() &&
      visitorAuthStorage.isAuthenticated() &&
      (typeof navigator === "undefined" || navigator.onLine !== false);

    if (online) {
      try {
        const idempotencyKey =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `scan-${Date.now()}`;
        const response = await scanApi.create(eventId, code, codeType, idempotencyKey);
        const mapped = mapApiResponse(response, userId);
        if (mapped.scanLog.companyId) {
          void engagementService.track({
            eventId,
            kind: "scan_contact",
            targetType: mapped.scanLog.scanTargetType,
            targetId: mapped.scanLog.scanTargetId,
            companyId: mapped.scanLog.companyId,
          });
        }
        return mapped;
      } catch (err) {
        const offline = await resolveOffline(eventId, userId, code, codeType);
        if (offline) return offline;
        throw err;
      }
    }

    const offline = await resolveOffline(eventId, userId, code, codeType);
    if (offline) return offline;
    throw new Error("[scanService] Offline and code not in local pack");
  },

  async unlock(
    companyId: string,
    options: { eventId?: string; userId?: string } = {},
  ): Promise<{ success: boolean; scanLog: ScanLog; savedContact?: SavedContact }> {
    const result = await this.scanCode(`company:${companyId}`, options);
    if (!result) throw new Error("Failed to scan");
    return {
      success: true,
      scanLog: result.scanLog,
      savedContact: result.savedContact,
    };
  },

  async getHistory(eventId?: string): Promise<ScanLog[]> {
    if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated() || !eventId) {
      return [];
    }
    try {
      const data = await scanApi.list(eventId);
      return data.scans.map(mapDtoToScanLog);
    } catch {
      return [];
    }
  },

  async isScanned(eventId: string, targetType: ScanTargetType, targetId: string): Promise<boolean> {
    const history = await this.getHistory(eventId);
    return history.some(
      (s) => s.scanTargetType === targetType && s.scanTargetId === targetId,
    );
  },
};

export type { ScanResult };
